import React from 'react';
import moment from 'moment';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import panelStyles, {CHANNEL_COLORS} from '../panelStyles';
import {formatMoney, BOOKING_STATUS} from '../panelMetrics';
import {NETWORK_RESERVATIONS, NETWORK_RESERVATION_DETAIL_SHORT} from '../../../../constants/appUrls.constants';

const initials = (name) => String(name || '?').trim().slice(0, 2).toUpperCase();

/** Son rezervasyonlar — kaydirilabilir liste, satirlar detaya gider.
 *  periodLabel: gecmis bir ay secildiyse donem adi, bugunun ayinda null. */
export default function RecentBookings({bookings, channelColorOf, periodLabel}) {
    const {t} = useTranslation();
    const classes = panelStyles();

    const statusPill = (booking) => {
        const code = Number(booking.status);
        if (code === BOOKING_STATUS.CANCELLED) {
            return <span className={`${classes.pill} ${classes.pillOff}`}><span className={classes.pillDot} />{t('enw-panel-status_cancelled', 'İptal')}</span>;
        }
        if (code === BOOKING_STATUS.PENDING) {
            return <span className={`${classes.pill} ${classes.pillWarn}`}><span className={classes.pillDot} />{t('enw-panel-status_pending', 'Ön rezervasyon')}</span>;
        }
        // No-show iptal degil: kayit gecerli, misafir gelmemis
        if (code === BOOKING_STATUS.NO_SHOW) {
            return <span className={`${classes.pill} ${classes.pillWarn}`}><span className={classes.pillDot} />{t('enw-panel-status_no_show', 'Gelmedi')}</span>;
        }
        if (code === BOOKING_STATUS.CONFIRMED) {
            return <span className={`${classes.pill} ${classes.pillOk}`}><span className={classes.pillDot} />{t('enw-panel-status_confirmed', 'Onaylı')}</span>;
        }
        // Tanimadigimiz bir durum: API'nin kendi adini yaz. Eskiden hepsi "Onayli"
        // gorunuyordu; backend yeni bir status eklerse kayit yanlis etiketlenirdi.
        const apiName = booking.booking_status && booking.booking_status.name;
        return (
            <span className={`${classes.pill} ${classes.pillOff}`}>
                <span className={classes.pillDot} />
                {apiName || `#${code}`}
            </span>
        );
    };

    return (
        <section className={classes.card}>
            <header className={classes.cardHead}>
                <div>
                    <h2 className={classes.cardTitle}>{t('enw-panel-recent_title', 'Son Rezervasyonlar')}</h2>
                    <p className={classes.cardDesc}>
                        {/* Gecmis ay secildiginde liste o ayla sinirli; "en yeni" ifadesi
                            tek basina bugunu cagristirdigi icin donem adi yaziliyor. */}
                        {periodLabel
                            ? t('enw-panel-recent_desc_period', {
                                defaultValue: '{{period}} ayının en yeni 10 kaydı',
                                period: periodLabel,
                            })
                            : t('enw-panel-recent_desc', 'En yeni 10 kayıt')}
                    </p>
                </div>
                <span style={{marginLeft: 'auto'}}>
                    <Link to={NETWORK_RESERVATIONS} className={classes.cardNote} style={{color: '#d71f26', fontWeight: 600, textDecoration: 'none'}}>
                        {t('enw-panel-see_all', 'Tümü')}
                    </Link>
                </span>
            </header>

            {bookings.length === 0 ? (
                <div className={classes.emptyBox}>{t('enw-panel-recent_empty', 'Bu dönemde rezervasyon yok.')}</div>
            ) : (
                <div className={classes.feed}>
                    {bookings.map((booking) => {
                        const channelName = (booking.booking_channel && booking.booking_channel.name) || '';
                        const cancelled = Number(booking.status) === BOOKING_STATUS.CANCELLED;
                        const nights = moment(booking.checkout_date, 'YYYY-MM-DD')
                            .diff(moment(booking.checkin_date, 'YYYY-MM-DD'), 'days');
                        const guest = booking.booking_contact
                            ? `${booking.booking_contact.name || ''} ${booking.booking_contact.surname || ''}`.trim()
                            : '';

                        return (
                            <Link
                                to={NETWORK_RESERVATION_DETAIL_SHORT + booking.id}
                                className={classes.res}
                                key={booking.id}
                            >
                                <span
                                    className={classes.resBadge}
                                    style={{background: cancelled ? '#8a95a5' : channelColorOf(channelName)}}
                                >
                                    {cancelled ? '—' : initials(channelName)}
                                </span>

                                <div style={{minWidth: 0}}>
                                    <div className={classes.resName}>
                                        {guest || booking.booking_code || '—'}
                                    </div>
                                    <div className={classes.resMeta}>
                                        <span>
                                            {moment(booking.checkin_date, 'YYYY-MM-DD').format('D MMM')}
                                            {' – '}
                                            {moment(booking.checkout_date, 'YYYY-MM-DD').format('D MMM')}
                                        </span>
                                        {nights > 0 && (
                                            <>
                                                <span className={classes.resSep}>·</span>
                                                <span>{t('enw-panel-night_count', {defaultValue: '{{count}} gece', count: nights})}</span>
                                            </>
                                        )}
                                        {statusPill(booking)}
                                    </div>
                                </div>

                                <div className={classes.resSide}>
                                    <div className={`${classes.resTotal} ${cancelled ? classes.resTotalCancelled : ''}`}>
                                        {formatMoney(booking.total)} {booking.currency_code}
                                    </div>
                                    <div className={classes.resTime}>
                                        {moment.unix(Number(booking.reservation_time)).fromNow()}
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </section>
    );
}

export {CHANNEL_COLORS};
